/**
 * Embedded HLS feed for the fan viewer.
 *
 *   - `onVideoEdgeUpdate` plumb-through to V2Player (video-end UTC,
 *     i.e. the newest playable media as PROGRAM-DATE-TIME; the caller
 *     derives lag against the newest GPS fix — lag.ts source 1)
 *   - `badge` overlay: LIVE (red, pulsing) vs REPLAY · Ln (cyan) per
 *     the mockup's .vid-state — flips when a past lap is selected.
 *
 * Every <FeedPlayer> mount is keyed on its session_id at the call sites,
 * so a V2Player instance is never reused across sessions (per-session
 * PDT anchors would corrupt UTC mapping).
 */

import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import api from '../../services/api';
import { mergeCoverageDelta } from '../../lib/coveragePaging';
import { SessionCoverage } from '../../types';
import { GlassRailData, PlayerTransport } from '../PlayerChrome';
import { EntryIntent } from './entryIntent';

/** A just-started recording's session row can land after the first request, so a
 *  404 inside this window is treated as "not yet" rather than "never". */
const STARTUP_404_GRACE_MS = 30_000;
const FIRST_FATAL_BACKOFF_MS = 5_000;
const MAX_FATAL_BACKOFF_MS = 60_000;
import { deriveRecordingStatus, RecordingStatus } from './recordingStatus';
import V2Player, { PlayerOverlayState, V2PlayerHandle } from './V2Player';
import { isSnapshotSettled } from './snapshotStatus';

/** Why there is no picture yet — what `renderStatus` hosts draw instead. */
export type FeedStatus =
  | { kind: 'no-session'; vod: boolean }
  | { kind: 'loading'; vod: boolean }
  | { kind: 'retired-format'; vod: boolean }
  | { kind: 'error'; vod: boolean; detail: string };

export interface VideoBadge {
  /** e.g. "LIVE", "REPLAY · L4", "REPLAY" */
  text: string;
  kind: 'live' | 'replay';
}

export const FeedPlayer: React.FC<{
  sessionId: string | null;
  playerRef?: React.MutableRefObject<V2PlayerHandle | null>;
  onUtcUpdate?: (utcMs: number | null) => void;
  /** Stream lag of the playing camera (wallclock − playhead PDT), s. */
  onVideoEdgeUpdate?: (videoEndUtcMs: number | null) => void;
  /** Fires once the mounted player's timeline anchor (first PROGRAM-
   *  DATE-TIME) is known; UTC ms. */
  onPdtReady?: (firstPdtMs: number) => void;
  initialSeekUtcMs?: number | null;
  /** Why this mount is opening where it is — required so a host can never
   *  silently default into the wrong start position (see `entryIntent.ts`).
   *  Only 'live' opens at the live edge; it also requires `initialSeekUtcMs`
   *  to be null — the point of 'live' is to ignore any saved position. */
  entryIntent: EntryIntent;
  /** Finished-event replay: keep the backend's is_active verdict so the
   *  player runs as VOD instead of live-tail polling for new segments. */
  vod?: boolean;
  /** Truthful status of the mounted session, derived from the same coverage
   *  fields `vod`/`live` already reconcile — never claims the phone is still
   *  recording once uploads are merely catching up. Fires on every coverage
   *  refresh; null while no verdict has arrived yet. */
  onRecordingStatus?: (status: RecordingStatus | null) => void;
  /** LIVE / REPLAY state badge overlaid top-left (mockup .vid-state). */
  badge?: VideoBadge | null;
  /** Optional bottom-right HUD chip (e.g. live speed). */
  cornerChip?: string | null;
  /** Theater: cap width so the 16:9 tile's height fits the viewport
   *  (video scales with the browser window instead of overflowing). */
  fill?: boolean;
  /** Height-bound the video tile (object-contain letterbox) instead of
   *  the default width-driven 16:9 box. Set on the mobile-landscape stage
   *  where the player area is short and wide — a width-driven box is far
   *  too tall and overflows/scrolls. Does NOT enter browser fullscreen. */
  heightFit?: boolean;
  /** Bubbles the auto-hiding playbar's visibility (see V2Player). */
  onControlsHiddenChange?: (hidden: boolean) => void;
  /** Override the Maximise button to fullscreen a parent (the landscape
   *  shell) instead of the player; `maximized` drives the button icon. */
  onMaximize?: () => void;
  maximized?: boolean;
  /** Fullscreen-landscape glass telemetry rail. */
  glassRail?: GlassRailData | null;
  /** Start playback once loaded (map-full inset remounts). */
  autoPlay?: boolean;
  /** Host-drawn controls over the picture in place of the playbar (see V2Player). */
  renderControls?: (transport: PlayerTransport, state: PlayerOverlayState) => React.ReactNode;
  /** Every coverage refresh of the mounted v2 session; null while there is none. */
  onCoverage?: (coverage: SessionCoverage | null) => void;
  /** Replaces the built-in text boxes shown while there is no playable v2 coverage. */
  renderStatus?: (status: FeedStatus) => React.ReactNode;
}> = ({
  sessionId,
  playerRef,
  onUtcUpdate,
  onVideoEdgeUpdate,
  onPdtReady,
  initialSeekUtcMs,
  entryIntent,
  vod = false,
  onRecordingStatus,
  badge = null,
  cornerChip = null,
  fill = false,
  heightFit = false,
  onControlsHiddenChange,
  onMaximize,
  maximized = false,
  glassRail = null,
  autoPlay = false,
  renderControls,
  onCoverage,
  renderStatus,
}) => {
  const [v2Coverage, setV2Coverage] = useState<SessionCoverage | null>(null);
  // A resolved coverage probe that did NOT report v2 — the session predates
  // media-contract-v2 and its playable format has been retired.
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastLoadedRef = useRef<string | null>(null);
  const v2ActiveRef = useRef(false);
  // Read inside the polling tick below without retriggering the effect that owns it.
  const v2CoverageRef = useRef<SessionCoverage | null>(null);
  // Read inside the tick without retriggering the setup effect below — only
  // sessionId identifies a distinct mount; the live→VOD settle must not
  // reset the coverage cursor or tear down the player.
  const vodRef = useRef(vod);
  vodRef.current = vod;
  // A fatal player error clears the mount so the poll tick can rebuild it. The
  // backoff keeps a persistent failure from rebuilding on every 5 s tick.
  const startupGraceUntilRef = useRef(0);
  const fatalRetryAtRef = useRef(0);
  const fatalBackoffRef = useRef(0);
  const nextFatalBackoffMs = () => {
    fatalBackoffRef.current = fatalBackoffRef.current
      ? Math.min(fatalBackoffRef.current * 2, MAX_FATAL_BACKOFF_MS)
      : FIRST_FATAL_BACKOFF_MS;
    return fatalBackoffRef.current;
  };
  // Once the backend reports the finalized snapshot complete AND the device no
  // longer active, there is nothing left to heal — stop polling regardless of
  // page mode. Cleared only on a session change; a settled VOD view must not
  // resume polling on its own.
  const v2PollStoppedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setV2Coverage(null);
      v2CoverageRef.current = null;
      setUnavailable(false);
      setError(null);
      lastLoadedRef.current = null;
      v2ActiveRef.current = false;
      v2PollStoppedRef.current = false;
      return;
    }
    let cancelled = false;
    setError(null);
    setV2Coverage(null);
    setUnavailable(false);
    v2CoverageRef.current = null;
    v2ActiveRef.current = false;
    v2PollStoppedRef.current = false;
    startupGraceUntilRef.current = Date.now() + STARTUP_404_GRACE_MS;
    fatalRetryAtRef.current = 0;
    fatalBackoffRef.current = 0;

    const applyV2Coverage = (coverage: SessionCoverage) => {
      v2CoverageRef.current = coverage;
      setV2Coverage(coverage);
      if (coverage.snapshot_complete === true && coverage.is_active === false) {
        v2PollStoppedRef.current = true;
      }
    };

    // Live v2: the unions grow (and heal) as artifacts land — refresh them in place, and
    // pick up a healed finalized snapshot (see the poll condition below for when this stops).
    // Cursored once a revision is known; `mergeCoverageDelta`'s `null` (a `reload` marker,
    // or a merge with no base) falls back to the same full fetch the first tick used.
    // Guards against a slow response still in flight when the next 5 s tick
    // fires — without it, two overlapping requests can resolve out of order
    // and apply the older one last.
    let refreshing = false;
    const refreshV2 = async () => {
      if (refreshing) return;
      refreshing = true;
      const base = v2CoverageRef.current;
      try {
        if (base?.coverage_rev === undefined) {
          const coverage = await api.getSessionCoverage(sessionId);
          if (!cancelled && coverage?.media_contract_version === 2) {
            applyV2Coverage(coverage);
          }
          return;
        }
        const delta = await api.getSessionCoveragePage(sessionId, base.coverage_rev, base.coverage_life);
        if (cancelled) return;
        const merged = mergeCoverageDelta(base, delta);
        if (merged) {
          applyV2Coverage(merged);
          return;
        }
        const coverage = await api.getSessionCoverage(sessionId);
        if (cancelled) return;
        if (coverage?.media_contract_version === 2) {
          applyV2Coverage(coverage);
        } else {
          // The recreated session isn't v2 yet (purge/recreate still
          // settling) — drop the stale cursor so the next tick does a plain
          // full fetch instead of repeating this reload every 5 s.
          v2CoverageRef.current = null;
        }
      } catch {
        /* transient — retry next tick, keep the last good coverage */
      } finally {
        refreshing = false;
      }
    };

    const load = async () => {
      let coverage: SessionCoverage | null = null;
      try {
        coverage = await api.getSessionCoverage(sessionId);
        if (cancelled) return;
      } catch (err) {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status !== 401 && status !== 404) {
          // Network/5xx — transient. Leave whatever is on screen (last good
          // coverage, or the pending/error state already showing) for the
          // next poll tick to retry.
          return;
        }
        if (status === 404 && Date.now() < startupGraceUntilRef.current) {
          // Still inside the startup window: the row may simply not exist yet.
          return;
        }
        // Unauthorized/not-found isn't transient — the session's contract
        // version is settled below the same way a resolved non-v2 verdict is.
      }
      if (coverage?.media_contract_version === 2) {
        v2ActiveRef.current = true;
        applyV2Coverage(coverage);
        setError(null);
        lastLoadedRef.current = sessionId;
        fatalRetryAtRef.current = 0;
        fatalBackoffRef.current = 0;
        return;
      }
      setUnavailable(true);
      lastLoadedRef.current = sessionId;
    };

    void load();
    // Poll while we haven't loaded yet — a fresh recording can take a few
    // seconds for its FleetSession row to land. Once v2 is active, keep
    // refreshing coverage while this is a live view or the latest snapshot
    // could still gain media — a finished, fully healed recording being
    // watched as VOD has nothing left to poll for.
    const id = window.setInterval(() => {
      if (lastLoadedRef.current !== sessionId) {
        if (Date.now() < fatalRetryAtRef.current) return;
        void load();
        return;
      }
      if (
        v2ActiveRef.current &&
        !v2PollStoppedRef.current &&
        (!vodRef.current || !isSnapshotSettled(v2CoverageRef.current))
      ) {
        void refreshV2();
      }
    }, 5_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [sessionId]);

  // Lag source 1 is "the newest playable media's PDT", which for a v2 session
  // is the ledger's own playable union — the same truth the playlists compose
  // from, already refreshed on the 5 s tick above. Reading it here keeps the
  // number playhead-independent (scrubbing back is not lag) without asking the
  // player to infer an edge from its buffer.
  useEffect(() => {
    if (!onVideoEdgeUpdate || !v2Coverage) return;
    const spans = v2Coverage.playable_union ?? v2Coverage.union ?? [];
    onVideoEdgeUpdate(spans.length ? spans[spans.length - 1][1] : null);
  }, [v2Coverage, onVideoEdgeUpdate]);

  useEffect(() => {
    if (!onRecordingStatus) return;
    onRecordingStatus(deriveRecordingStatus(v2Coverage));
  }, [v2Coverage, onRecordingStatus]);

  useEffect(() => {
    onCoverage?.(v2Coverage);
  }, [v2Coverage, onCoverage]);

  // The persistent V2Player judged its master unrecoverable (bounded retries
  // exhausted — e.g. a 404 that never resolves). Surface a clear error
  // instead of the player's own infinite-spinner failure mode. The next 5s
  // tick's `refreshV2` above will remount it if coverage still says v2 and
  // the underlying issue has cleared — a slow, visible retry, not a tight loop.
  const handleV2FatalError = (detail: string) => {
    v2ActiveRef.current = false;
    setV2Coverage(null);
    setError(`v2 playback unavailable — ${detail}`);
    // Both of these must be cleared or the poll tick takes neither branch:
    // `lastLoadedRef` still names this session, and `v2ActiveRef` is now false.
    // A transient failure would strand the viewer until they navigated away.
    v2CoverageRef.current = null;
    lastLoadedRef.current = null;
    fatalRetryAtRef.current = Date.now() + nextFatalBackoffMs();
  };

  if (!sessionId) {
    if (renderStatus) return <>{renderStatus({ kind: 'no-session', vod })}</>;
    return (
      <div className="flex h-56 items-center justify-center bg-bg-video text-xs text-fg-secondary">
        {vod ? 'no recording for this device' : 'not live right now'}
      </div>
    );
  }
  if (v2Coverage) {
    // media-contract-v2: one persistent player for the whole session. The master URL stays
    // stable; unions and snapshotRev refresh as props, and V2Player reloads in place only when
    // snapshotRev changes.
    //
    // Explicit VOD mode always wins; backend truth only ever settles a LIVE
    // page into VOD once the device stops recording — never the reverse, so
    // a caller that already committed to VOD can't be pulled back to live by
    // a stale/absent is_active.
    const live = !vod && (v2Coverage.is_active ?? true);
    // Open at the live edge only when the HOST declared a live entry, not
    // merely because coverage happens to say the device is still active —
    // e.g. the Driver-tab compare player is always `entryIntent="explicit"`
    // even if it lands on a still-recording session; it must never auto-jump
    // away from its seeded comparison point.
    const startAtLiveEdge = live && entryIntent === 'live';
    return (
      <div
        className={cn(
          // flex column so PlayerChrome's flex-grow media area actually takes
          // the height this box defines.
          'relative flex flex-col bg-bg-video',
          !heightFit && 'aspect-video',
          heightFit && 'h-full min-h-0',
          fill && 'mx-auto w-full',
        )}
        style={fill ? { maxWidth: 'calc((100dvh - 150px) * 1.7778)' } : undefined}
      >
        {badge && (
          <div
            className={cn(
              'feed-video-badge absolute left-2 top-2 z-10 flex items-center gap-1.5 rounded bg-black/70 px-2 py-1 text-[10px] font-bold',
              badge.kind === 'live' ? 'text-white' : 'text-accent-cyan',
            )}
          >
            <span
              className={cn(
                'inline-block h-1.5 w-1.5 rounded-full',
                badge.kind === 'live' ? 'animate-pulse bg-status-error' : 'bg-accent-cyan',
              )}
            />
            {badge.text}
          </div>
        )}
        {cornerChip && (
          <div className="absolute bottom-2 right-2 z-10 rounded bg-black/70 px-2 py-1 font-mono text-[11px] text-white">
            {cornerChip}
          </div>
        )}
        <V2Player
          ref={playerRef}
          masterUrl={`/api/fleet/sessions/${encodeURIComponent(sessionId ?? '')}/v2/master.m3u8`}
          audioUnion={v2Coverage.audio_union}
          videoUnion={v2Coverage.video_union}
          playableUnion={v2Coverage.playable_union ?? v2Coverage.union}
          snapshotRev={v2Coverage.snapshot_rev ?? null}
          coverageLife={v2Coverage.coverage_life ?? null}
          audioExpected={v2Coverage.audio_expected ?? null}
          rotation={v2Coverage.rotation ?? null}
          live={live}
          startAtLiveEdge={startAtLiveEdge}
          initialSeekUtcMs={initialSeekUtcMs}
          entryIntent={entryIntent}
          // Muted so autoplay policy permits it; a live view attempts it
          // unprompted even when the caller hasn't asked for autoPlay.
          muted
          autoPlay={autoPlay || live}
          chrome="minimal"
          heightFit={heightFit}
          glassRail={glassRail}
          onMaximize={onMaximize}
          maximized={maximized}
          onControlsHiddenChange={onControlsHiddenChange}
          onUtcUpdate={onUtcUpdate}
          onPdtReady={onPdtReady}
          onFatalError={handleV2FatalError}
          renderControls={renderControls}
        />
      </div>
    );
  }
  if (unavailable) {
    if (renderStatus) return <>{renderStatus({ kind: 'retired-format', vod })}</>;
    return (
      <div className="flex h-56 items-center justify-center bg-bg-video px-4 text-center text-xs text-fg-secondary">
        This session was recorded under the retired legacy format and can no longer be played.
      </div>
    );
  }
  if (renderStatus) {
    return <>{renderStatus(error ? { kind: 'error', vod, detail: error } : { kind: 'loading', vod })}</>;
  }
  return (
    <div className="flex h-56 items-center justify-center bg-bg-video text-xs text-fg-secondary">
      {error ?? 'loading feed…'}
    </div>
  );
};

export default FeedPlayer;
